import {
  ClockCircleOutlined,
  RetweetOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Flex, Typography } from "antd";
import { CSSProperties } from "react";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatDate } from "shared/utils/dayjs";
import { CalendarSchedule } from "./types";
import { formatToLocalDateRange } from "./utils";
import { textPx } from "shared/constants/zoom";

// Up to three clients are named (a private, duet or triplet: the identity IS
// the information); a bigger class shows the count, like the block does.
const NAMED_CLIENT_CAP = 3;

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: textPx(12.5),
  color: colors.ink2,
  marginTop: 9,
};
const iconStyle: CSSProperties = { color: colors.ink3, fontSize: textPx(13) };

export const hoverCardBodyStyle: CSSProperties = {
  width: 252,
  borderRadius: 14,
  padding: "14px 15px",
  border: `1px solid ${colors.line}`,
  boxShadow: "0 12px 32px -8px rgba(15, 15, 14, 0.18)",
};

/**
 * The card a calendar block shows on hover: the class with its instructor's
 * colour dot, then instructor, clients, date and time, and whether it repeats.
 * The icons live here; the block itself carries none.
 */
export const EventHoverCard = ({
  event,
  dotColor,
}: {
  event: CalendarSchedule;
  dotColor: string;
}) => {
  const customerNames = Array.isArray(event.customer_names)
    ? event.customer_names
    : event.customer_name
    ? [event.customer_name]
    : [];
  return (
    <div>
      <Flex align="center" gap={8}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: dotColor,
            flexShrink: 0,
          }}
        />
        <Typography.Text
          strong
          style={{
            fontFamily: "var(--font-display)",
            fontSize: textPx(14),
            letterSpacing: "-0.01em",
          }}
        >
          {event.class_name ?? event.title}
        </Typography.Text>
      </Flex>
      <div style={rowStyle}>
        <UserOutlined style={iconStyle} />
        {event.instructor_name}
      </div>
      <div style={rowStyle}>
        <TeamOutlined style={iconStyle} />
        {/* On a private, duet or triplet the identity IS the information.
            "1 Klien" forced an admin to open the schedule just to learn
            who it was, when the name was already in the payload. */}
        {customerNames.length > 0 && customerNames.length <= NAMED_CLIENT_CAP
          ? customerNames.join(", ")
          : translateWithVars("schedule.grid.participants_n", {
              n: customerNames.length,
            })}
      </div>
      <div style={rowStyle}>
        <ClockCircleOutlined style={iconStyle} />
        {formatDate(event.start_time, "DD MMM YYYY")} ·{" "}
        {formatToLocalDateRange(event.start_time, event.end_time)}
      </div>
      {event.is_recurring && (
        <div style={rowStyle}>
          <RetweetOutlined style={iconStyle} />
          {translate("schedule.grid.recurring")}
        </div>
      )}
    </div>
  );
};
